import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import IconArrowHead from '../../../../IconArrowHead/IconArrowHead';
import RangeFilterForm from './RangeFilterForm';

import css from './RangeFilterPlain.module.css';

const RADIX = 10;

const getQueryParamName = queryParamNames =>
  Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'range';

const parse = rangeStr => {
  const [min, max] = !!rangeStr ? rangeStr.split(',').map(v => Number.parseFloat(v, RADIX)) : [];
  return !!rangeStr && min != null && max != null ? { min, max } : null;
};

const format = (range, queryParamName) => {
  const { min, max } = range || {};
  const value = min != null && max != null ? `${min},${max}` : null;
  return { [queryParamName]: value };
};

const RangeFilterPlain = ({
  rootClassName,
  className,
  id,
  label,
  queryParamNames,
  initialValues,
  min,
  max,
  step,
  onSubmit,
  onBlur,
  unit,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const paramName = getQueryParamName(queryParamNames);
  const parsed = initialValues ? parse(initialValues[paramName]) : {};
  const { min: minVal, max: maxVal } = parsed || {};

  const hasValue = v => v != null;
  const hasInitialValues = initialValues && hasValue(minVal) && hasValue(maxVal);

  const labelSelection = hasInitialValues
    ? `${label}: ${minVal}${unit || ''} – ${maxVal}${unit || ''}`
    : label;

  const handleChange = values => {
    onSubmit(format(values, paramName));
  };

  const handleClear = () => {
    onSubmit(format(null, paramName));
  };

  const toggleIsOpen = () => {
    setIsOpen(prev => !prev);
  };

  const handleClickOutside = e => {
    if (isOpen && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsOpen(false);
      onBlur?.();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const classes = classNames(rootClassName || css.root, { [css.rootOpen]: isOpen }, className);

  return (
    <div className={classes} ref={dropdownRef}>
      <div className={css.filterHeader}>
        <button type="button" className={css.labelButton} onClick={toggleIsOpen}>
          <span className={css.labelButtonContent}>
            <span className={css.labelWrapper}>
              <span className={css.label}>{labelSelection}</span>
            </span>
            <span className={css.openSign}>
              <IconArrowHead direction={isOpen ? 'up' : 'down'} />
            </span>
          </span>
        </button>
      </div>

      <div className={classNames(css.formWrapper, { [css.formClosed]: !isOpen })}>
        <RangeFilterForm
          id={id}
          initialValues={hasInitialValues ? parsed : { min: min, max: max }}
          onChange={handleChange}
          onCancel={() => setIsOpen(false)}
          onSubmit={handleChange}
          onClear={handleClear}
          intl={intl}
          min={min}
          max={max}
          step={step}
          unit={unit}
          isInSideBar
        />
      </div>
    </div>
  );
};

export default RangeFilterPlain;
